import { apiHelpers } from './axios'
import { stringToUserRole } from './permissions'
import type {
  ApiResponse,
  User,
  LoginCredentials,
  Post,
  CreatePostData,
  UpdatePostData,
  GetRolesResponse,
  InviteUserPayload,
  InviteUserResponse,
  GetInvitationInfoResponse,
  CompleteInvitationPayload,
  Event,
  CreateEventPayload,
  GetEventsResponse,
  CreateBookingPayload,
  CreateBookingResponse,
  GetEventDetailResponse,
  PaginationInfo,
} from '@/types'

// Feature flags for authentication features
// These can be enabled when backend support is added
const AUTH_FEATURES = {
  REFRESH_TOKEN_ENABLED: false, // Backend does not support refresh tokens yet
  PROFILE_FETCH_ENABLED: false, // Backend does not support profile endpoint yet
}

// Authentication API services
export const authService = {
  // Login user
  login: async (
    credentials: LoginCredentials
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/login',
        credentials
      )

      // Check if response has statusCode 200 (successful login)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Store token in localStorage if login successful
        if ('token' in response && typeof window !== 'undefined') {
          const token = response.token as string
          localStorage.setItem('auth-token', token)

          // Production debugging
          if (process.env.NODE_ENV === 'production') {
            console.log('Login: Token stored in localStorage', {
              tokenLength: token.length,
            })
          }
        }

        // Transform the response to match expected format
        const user: User = {
          id: (response as any).userId as string,
          email: (response as any).email as string,
          name: (response as any).fullName as string,
          role: stringToUserRole((response as any).roles?.[0]?.name || 'user'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        return {
          success: true,
          data: {
            user,
            token: (response as any).token as string,
          },
        }
      } else {
        // Handle error responses
        return {
          success: false,
          error: response?.message?.[0] || 'Login failed',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Login failed',
      }
    }
  },

  // Get invitation info
  getInvitationInfo: async (
    token: string
  ): Promise<ApiResponse<GetInvitationInfoResponse>> => {
    try {
      const response = await apiHelpers.get(
        `/api/advancing/user/get-invitation-info?Token=${token}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response as unknown as GetInvitationInfoResponse,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to get invitation info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get invitation info',
      }
    }
  },

  // Complete invitation (register user)
  register: async (
    invitationData: CompleteInvitationPayload
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/complete-invitation',
        invitationData
      )

      // Check if response has statusCode 200 (successful registration)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Store token in localStorage if registration successful
        if ('token' in response && typeof window !== 'undefined') {
          const token = response.token as string
          localStorage.setItem('auth-token', token)

          // Production debugging
          if (process.env.NODE_ENV === 'production') {
            console.log('Registration: Token stored in localStorage', {
              tokenLength: token.length,
            })
          }
        }

        // Transform the response to match expected format
        const user: User = {
          id: (response as any).userId as string,
          email: (response as any).email as string,
          name: (response as any).fullName as string,
          role: stringToUserRole((response as any).roles?.[0]?.name || 'user'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        return {
          success: true,
          data: {
            user,
            token: (response as any).token as string,
          },
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Registration failed',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Registration failed',
      }
    }
  },

  // Send password reset email
  sendPasswordReset: async (email: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/init-password-reset',
        { email }
      )

      // Check if response has statusCode 200 (successful)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to send reset email',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send reset email',
      }
    }
  },

  // Logout user (frontend only - no server call needed)
  logout: (): void => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth-token')
    }
  },

  // Get current user profile
  // NOTE: Currently disabled - backend does not support profile endpoint yet
  getProfile: async (): Promise<ApiResponse<User>> => {
    if (!AUTH_FEATURES.PROFILE_FETCH_ENABLED) {
      return {
        success: false,
        error: 'Profile fetching is currently disabled - backend support not available',
      }
    }
    return apiHelpers.get('/auth/profile')
  },

  // Refresh auth token
  // NOTE: Currently disabled - backend does not support refresh tokens yet
  // Token expires in 72 hours, users must login again after expiration
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    if (!AUTH_FEATURES.REFRESH_TOKEN_ENABLED) {
      return {
        success: false,
        error: 'Token refresh is currently disabled - backend support not available',
      }
    }

    try {
      const response = await apiHelpers.post('/api/advancing/auth/refresh')

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Store new token in localStorage
        if ('token' in response && typeof window !== 'undefined') {
          const token = (response as { token: string }).token
          localStorage.setItem('auth-token', token)

          if (process.env.NODE_ENV === 'production') {
            console.log('Token refresh: New token stored', {
              tokenLength: token.length,
            })
          }
        }

        const tokenResponse = response as unknown as {
          token: string
          statusCode: number
        }
        return {
          success: true,
          data: { token: tokenResponse.token },
        }
      } else {
        return {
          success: false,
          error: (response as any)?.message?.[0] || 'Token refresh failed',
        }
      }
    } catch (error: unknown) {
      return {
        success: false,
        error: (error as Error).message || 'Token refresh failed',
      }
    }
  },

  // Check if token needs refresh (based on expiration)
  // NOTE: Currently disabled - backend does not support refresh tokens yet
  shouldRefreshToken: (): boolean => {
    if (!AUTH_FEATURES.REFRESH_TOKEN_ENABLED) {
      return false
    }

    if (typeof window === 'undefined') return false

    const token = localStorage.getItem('auth-token')
    if (!token) return false

    try {
      // Decode JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      const bufferTime = 300 // Refresh 5 minutes before expiration

      return payload.exp && payload.exp - currentTime < bufferTime
    } catch {
      // If token is malformed, consider it expired
      return true
    }
  },

  // Get token expiration time
  getTokenExpiration: (): number | null => {
    if (typeof window === 'undefined') return null

    const token = localStorage.getItem('auth-token')
    if (!token) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.exp ? payload.exp * 1000 : null
    } catch {
      return null
    }
  },

  // Send magic link
  sendMagicLink: async (
    email: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/request-magic-link',
        { email }
      )

      // Check if response has statusCode 200 (like other API functions)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: { message: 'Magic link sent successfully' },
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to send magic link',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send magic link',
      }
    }
  },

  // Consume magic link token
  consumeMagicLink: async (
    token: string
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/consume-magic-link',
        { token }
      )

      // Check if response has statusCode 200 (successful authentication)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Store token in localStorage if authentication successful
        if ('token' in response && typeof window !== 'undefined') {
          const token = response.token as string
          localStorage.setItem('auth-token', token)

          // Production debugging
          if (process.env.NODE_ENV === 'production') {
            console.log('Magic Link: Token stored in localStorage', {
              tokenLength: token.length,
            })
          }
        }

        // Transform the response to match expected format
        const user: User = {
          id: (response as any).userId as string,
          email: (response as any).email as string,
          name: (response as any).fullName as string,
          role: stringToUserRole((response as any).roles?.[0]?.name || 'user'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        return {
          success: true,
          data: {
            user,
            token: (response as any).token as string,
          },
        }
      } else {
        // Handle error responses
        return {
          success: false,
          error: response?.message?.[0] || 'Magic link authentication failed',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Magic link authentication failed',
      }
    }
  },
}

// Posts/Content API services (example CRUD operations)
export const postService = {
  // Get all posts
  getAllPosts: async (
    page = 1,
    limit = 10
  ): Promise<ApiResponse<{ posts: Post[]; total: number; pages: number }>> => {
    return apiHelpers.get(`/posts?page=${page}&limit=${limit}`)
  },

  // Get single post by ID
  getPost: async (id: string): Promise<ApiResponse<Post>> => {
    return apiHelpers.get(`/posts/${id}`)
  },

  // Create new post
  createPost: async (data: CreatePostData): Promise<ApiResponse<Post>> => {
    return apiHelpers.post('/posts', data)
  },

  // Update existing post
  updatePost: async (data: UpdatePostData): Promise<ApiResponse<Post>> => {
    return apiHelpers.put(`/posts/${data.id}`, data)
  },

  // Delete post
  deletePost: async (id: string): Promise<ApiResponse> => {
    return apiHelpers.delete(`/posts/${id}`)
  },

  // Get posts by user
  getUserPosts: async (userId: string): Promise<ApiResponse<Post[]>> => {
    return apiHelpers.get(`/posts/user/${userId}`)
  },
}

// Example utility functions
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  },

  // Check if response is successful
  isSuccess: (response: ApiResponse): boolean => {
    return response.success === true
  },

  // Extract data from response safely
  extractData: <T>(response: ApiResponse<T>): T | null => {
    return response.success ? response.data || null : null
  },
}

// User Management API services
export const userService = {
  // Get all available roles
  getRoles: async (): Promise<ApiResponse<GetRolesResponse>> => {
    try {
      const response = await apiHelpers.get('/api/advancing/role/get-all')

      // Backend API response is returned directly, check statusCode
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response as unknown as GetRolesResponse,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch roles',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch roles',
      }
    }
  },

  // Invite user
  inviteUser: async (
    payload: InviteUserPayload
  ): Promise<ApiResponse<InviteUserResponse>> => {
    console.log('[userService.inviteUser] API call initiated', {
      timestamp: new Date().toISOString(),
      payload,
      endpoint: '/api/advancing/user/invite',
    })
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/invite',
        payload
      )
      console.log('[userService.inviteUser] API response received:', response)

      // Backend API response is returned directly, check statusCode
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response as unknown as InviteUserResponse,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to invite user',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to invite user',
      }
    }
  },

  // Get invited users
  getInvitedUsers: async (
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams()
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const url = queryParams.toString()
        ? `/api/advancing/user/get-invited-users?${queryParams.toString()}`
        : `/api/advancing/user/get-invited-users`

      const response = await apiHelpers.get(url)

      // Backend API response is returned directly, check statusCode
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch invited users',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch invited users',
      }
    }
  },

  // Get registered users
  getRegisteredUsers: async (
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams()
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const url = queryParams.toString()
        ? `/api/advancing/user/get-registered-users?${queryParams.toString()}`
        : `/api/advancing/user/get-registered-users`

      const response = await apiHelpers.get(url)

      // Backend API response is returned directly, check statusCode
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch registered users',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch registered users',
      }
    }
  },

  // Reset password with token
  resetPassword: async (
    token: string,
    password: string
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/user/reset-password',
        { token, password }
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to reset password',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to reset password',
      }
    }
  },
}

// Event API services
export const eventService = {
  // Get all events
  getAllEvents: async (
    userId: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<{ events: Event[]; pagination: PaginationInfo }>> => {
    try {
      const queryParams = new URLSearchParams({ userId })
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const response = await apiHelpers.get(`/api/advancing/event/get-all?${queryParams.toString()}`)

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Transform the response to match expected format
        const responseData = response as unknown as GetEventsResponse
        return {
          success: true,
          data: {
            events: responseData.events || [],
            pagination: (responseData as any).pagination || {
              page: page || 1,
              pageSize: pageSize || 10,
              totalCount: responseData.events?.length || 0,
              totalPages: 1,
              hasPrevious: false,
              hasNext: false,
            },
          },
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch events',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch events',
      }
    }
  },

  // Create new event
  createEvent: async (
    payload: CreateEventPayload
  ): Promise<ApiResponse<Event>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/register',
        payload
      )

      // Check if response has statusCode 200 or 201
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        // API successfully created event, return success
        // The calling code will refresh the events list to get real data
        return {
          success: true,
          data: undefined, // No event data returned, caller will refresh list
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to create event',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create event',
      }
    }
  },

  // Get event by ID with details and related bookings
  getEventById: async (
    userId: string,
    eventId: string
  ): Promise<ApiResponse<GetEventDetailResponse>> => {
    try {
      const response = await apiHelpers.get(
        `/api/advancing/event/get-by-id?UserId=${userId}&EventId=${eventId}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        // Transform the response to match expected format
        const responseData = response as unknown as GetEventDetailResponse
        return {
          success: true,
          data: responseData,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch event details',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch event details',
      }
    }
  },

  // Update event
  updateEvent: async (payload: {
    userId: string
    eventId: string
    eventName: string
    eventDate: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update event',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update event',
      }
    }
  },
}

// Hotel API services
export const hotelService = {
  // Get all hotels
  getAllHotels: async (): Promise<
    ApiResponse<{ id: string; name: string }[]>
  > => {
    try {
      const response = await apiHelpers.get('/api/advancing/hotels/get-all')

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: (response as any).hotels || [],
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch hotels',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch hotels',
      }
    }
  },
}

// Booking API services
export const bookingService = {
  // Create new booking
  createBooking: async (
    payload: CreateBookingPayload
  ): Promise<ApiResponse<CreateBookingResponse>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/register',
        payload
      )

      // Check if response has statusCode 200 or 201
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        // Transform the response to match expected format
        const responseData = response as unknown as CreateBookingResponse
        return {
          success: true,
          data: responseData,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to create booking',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create booking',
      }
    }
  },

  // Add agent(s) to booking / send agent request
  addAgentToBooking: async (payload: {
    userId: string
    bookingId: string
    agentEmails: string[]
    deadline: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/add-agent',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to add agent(s) to booking',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to add agent(s) to booking',
      }
    }
  },

  // Get manager bookings list
  getManagerBookings: async (
    userId: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams({ userId })
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const response = await apiHelpers.get(
        `/api/advancing/event/booking/get-manager-bookings?${queryParams.toString()}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch manager bookings',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch manager bookings',
      }
    }
  },

  // Get agent bookings list
  getAgentBookings: async (
    userId: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams({ userId })
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const response = await apiHelpers.get(
        `/api/advancing/event/booking/get-agent-bookings?${queryParams.toString()}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch agent bookings',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch agent bookings',
      }
    }
  },

  // Get manager booking details
  getManagerBooking: async (
    userId: string,
    bookingId: string
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get(
        `/api/advancing/event/booking/get-manager-booking?userId=${userId}&bookingId=${bookingId}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to fetch booking details',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch booking details',
      }
    }
  },

  // Update main booking info
  updateBooking: async (payload: {
    userId: string
    bookingId: string
    artistProfileId?: string
    artistName: string
    eventName: string
    eventDate: string
    setStartTime: { hour: number; minute: number }
    setEndTime: { hour: number; minute: number }
    room: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update booking',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update booking',
      }
    }
  },

  // Register legal info
  registerLegalInfo: async (payload: {
    userId: string
    bookingId: string
    artistLegalName: string
    artistEmail: string
    artistContactName: string
    idExpirationDate: string
    Phone?: string
    InstagramUrl?: string
    StageName?: string
    legalDocumentNumber?: string
    documents?: File[]
    updateProfile?: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('userId', payload.userId)
      formData.append('bookingId', payload.bookingId)
      formData.append('artistLegalName', payload.artistLegalName)
      formData.append('artistEmail', payload.artistEmail)
      formData.append('artistContactName', payload.artistContactName)
      formData.append('idExpirationDate', payload.idExpirationDate)

      // Append new fields if provided
      if (payload.Phone) {
        formData.append('Phone', payload.Phone)
      }
      if (payload.InstagramUrl) {
        formData.append('InstagramUrl', payload.InstagramUrl)
      }
      if (payload.StageName) {
        formData.append('StageName', payload.StageName)
      }
      if (payload.legalDocumentNumber) {
        formData.append('legalDocumentNumber', payload.legalDocumentNumber)
      }

      // Append updateProfile if provided
      if (payload.updateProfile !== undefined) {
        formData.append('updateProfile', String(payload.updateProfile))
      }

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/advancing/event/booking/legal-info-register',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register legal info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register legal info',
      }
    }
  },

  // Update legal info
  updateLegalInfo: async (payload: {
    userId: string
    bookingId: string
    legalInfoId: string
    artistLegalName: string
    artistEmail: string
    artistContactName: string
    idExpirationDate: string
    Phone?: string
    InstagramUrl?: string
    StageName?: string
    legalDocumentNumber?: string
    documents?: File[]
    updateProfile?: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('userId', payload.userId)
      formData.append('bookingId', payload.bookingId)
      formData.append('legalInfoId', payload.legalInfoId)
      formData.append('artistLegalName', payload.artistLegalName)
      formData.append('artistEmail', payload.artistEmail)
      formData.append('artistContactName', payload.artistContactName)
      formData.append('idExpirationDate', payload.idExpirationDate)

      // Append new fields if provided
      if (payload.Phone) {
        formData.append('Phone', payload.Phone)
      }
      if (payload.InstagramUrl) {
        formData.append('InstagramUrl', payload.InstagramUrl)
      }
      if (payload.StageName) {
        formData.append('StageName', payload.StageName)
      }
      if (payload.legalDocumentNumber) {
        formData.append('legalDocumentNumber', payload.legalDocumentNumber)
      }

      // Append updateProfile if provided
      if (payload.updateProfile !== undefined) {
        formData.append('updateProfile', String(payload.updateProfile))
      }

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/advancing/event/booking/legal-info-update',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update legal info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update legal info',
      }
    }
  },

  // Register technical requirements
  registerTechnicalReq: async (payload: {
    userId: string
    bookingId: string
    mixerType: string
    turntables2X: string
    additionalTurntables2X: string
    comment: string
    documents?: File[]
    updateProfile?: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('userId', payload.userId)
      formData.append('bookingId', payload.bookingId)
      formData.append('mixerType', payload.mixerType)
      formData.append('turntables2X', payload.turntables2X)
      formData.append('additionalTurntables2X', payload.additionalTurntables2X)
      formData.append('comment', payload.comment)

      // Append updateProfile if provided
      if (payload.updateProfile !== undefined) {
        formData.append('updateProfile', String(payload.updateProfile))
      }

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/advancing/event/booking/technical-req-register',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] ||
            'Failed to register technical requirements',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register technical requirements',
      }
    }
  },

  // Update technical requirements
  updateTechnicalReq: async (payload: {
    userId: string
    bookingId: string
    technicalRequirementId: string
    mixerType: string
    turntables2X: string
    additionalTurntables2X: string
    comment: string
    documents?: File[]
    updateProfile?: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('userId', payload.userId)
      formData.append('bookingId', payload.bookingId)
      formData.append('technicalRequirementId', payload.technicalRequirementId)
      formData.append('mixerType', payload.mixerType)
      formData.append('turntables2X', payload.turntables2X)
      formData.append('additionalTurntables2X', payload.additionalTurntables2X)
      formData.append('comment', payload.comment)

      // Append updateProfile if provided
      if (payload.updateProfile !== undefined) {
        formData.append('updateProfile', String(payload.updateProfile))
      }

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/advancing/event/booking/technical-req-update',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] || 'Failed to update technical requirements',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update technical requirements',
      }
    }
  },

  // Register rider notes
  registerBookingRiderNotes: async (payload: {
    userId: string
    bookingId: string
    value: string
    updateProfile: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/rider-notes-register',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register rider notes',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register rider notes',
      }
    }
  },

  // Update rider notes
  updateBookingRiderNotes: async (payload: {
    userId: string
    riderNotesId: string
    value: string
    updateProfile: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/rider-notes-update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update rider notes',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update rider notes',
      }
    }
  },

  // Register food restrictions
  registerBookingFoodRestrictions: async (payload: {
    userId: string
    bookingId: string
    value: string
    updateProfile: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/food-restrictions-register',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register food restrictions',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register food restrictions',
      }
    }
  },

  // Update food restrictions
  updateBookingFoodRestrictions: async (payload: {
    userId: string
    foodRestrictionsId: string
    value: string
    updateProfile: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/food-restrictions-update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update food restrictions',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update food restrictions',
      }
    }
  },

  // Register hotel info
  registerHotelInfo: async (payload: {
    userId: string
    bookingId: string
    hotelName: string
    checkInDate: string
    checkOutDate: string
    earlyCheckIn: boolean
    lateCheckOut: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/hotel-info-register',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register hotel info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register hotel info',
      }
    }
  },

  // Update hotel info
  updateHotelInfo: async (payload: {
    userId: string
    bookingId: string
    bookingHotelId: string
    hotelName: string
    checkInDate: string
    checkOutDate: string
    earlyCheckIn: boolean
    lateCheckOut: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/hotel-info-update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update hotel info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update hotel info',
      }
    }
  },

  // Register guest list
  registerGuestList: async (payload: {
    userId: string
    bookingId: string
    guestList: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/guest-list-register',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register guest list',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register guest list',
      }
    }
  },

  // Update guest list
  updateGuestList: async (payload: {
    userId: string
    guestListId: string
    guestList: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/guest-list-update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update guest list',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update guest list',
      }
    }
  },

  // Register sound check
  registerSoundCheck: async (payload: {
    userId: string
    bookingId: string
    soundCheckDate: string
    soundCheckTime: { hour: number; minute: number }
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/sound-check-register',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to register sound check',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to register sound check',
      }
    }
  },

  // Update sound check
  updateSoundCheck: async (payload: {
    userId: string
    bookingId: string
    bookingSoundCheckId: string
    soundCheckDate: string
    soundCheckTime: { hour: number; minute: number }
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/sound-check-update',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update sound check',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update sound check',
      }
    }
  },

  // Request booking hotel
  requestBookingHotel: async (payload: {
    userId: string
    bookingId: string
    checkInDate: string
    checkOutDate: string
    earlyCheckIn: boolean
    lateCheckOut: boolean
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/request-booking-hotel',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to request booking hotel',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to request booking hotel',
      }
    }
  },

  // Request booking soundcheck
  requestBookingSoundcheck: async (payload: {
    userId: string
    bookingId: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/request-booking-soundcheck',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] || 'Failed to request booking soundcheck',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to request booking soundcheck',
      }
    }
  },

  // Get agent booking details
  getAgentBooking: async (
    userId: string,
    bookingId: string
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get(
        `/api/advancing/event/booking/get-agent-booking?userId=${userId}&bookingId=${bookingId}`
      )

      // Check if response has statusCode 200
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] || 'Failed to fetch agent booking details',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch agent booking details',
      }
    }
  },

  // Send booking to agent
  sendToAgent: async (payload: {
    userId: string
    bookingId: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/send-to-agent',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to send booking to agent',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send booking to agent',
      }
    }
  },

  // Mark booking as agent completed
  agentCompleted: async (payload: {
    userId: string
    bookingId: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/agent-completed',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] ||
            'Failed to mark booking as agent completed',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to mark booking as agent completed',
      }
    }
  },

  // Send booking to finalise
  sendToFinalise: async (payload: {
    userId: string
    bookingId: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/send-to-finalise',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to send booking to finalise',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to send booking to finalise',
      }
    }
  },

  // Finalise booking (approve or reject)
  finaliseBooking: async (payload: {
    userId: string
    bookingId: string
    approve: boolean
    comment: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/finalise-booking',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to finalise booking',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to finalise booking',
      }
    }
  },

  // Delete legal document
  deleteLegalDocument: async (payload: {
    userId: string
    legalDocumentId: string
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('deleteLegalDocument API call with payload:', payload)

      const response = await apiHelpers.post(
        '/api/advancing/event/booking/delete-legal-document',
        payload
      )

      console.log('deleteLegalDocument API response:', response)

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        // Better error message extraction
        let errorMessage = 'Failed to delete legal document'
        if (response?.message) {
          if (Array.isArray(response.message)) {
            errorMessage = response.message[0] || errorMessage
          } else if (typeof response.message === 'string') {
            errorMessage = response.message
          }
        }

        console.error('deleteLegalDocument failed:', {
          statusCode: response?.statusCode,
          message: response?.message,
          fullResponse: response,
        })

        return {
          success: false,
          error: errorMessage,
        }
      }
    } catch (error: any) {
      console.error('deleteLegalDocument exception:', error)
      return {
        success: false,
        error: error.message || 'Failed to delete legal document',
      }
    }
  },

  // Delete technical document
  deleteTechnicalDocument: async (payload: {
    userId: string
    technicalDocumentId: string
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('deleteTechnicalDocument API call with payload:', payload)

      const response = await apiHelpers.post(
        '/api/advancing/event/booking/delete-technical-document',
        payload
      )

      console.log('deleteTechnicalDocument API response:', response)

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        // Better error message extraction
        let errorMessage = 'Failed to delete technical document'
        if (response?.message) {
          if (Array.isArray(response.message)) {
            errorMessage = response.message[0] || errorMessage
          } else if (typeof response.message === 'string') {
            errorMessage = response.message
          }
        }

        console.error('deleteTechnicalDocument failed:', {
          statusCode: response?.statusCode,
          message: response?.message,
          fullResponse: response,
        })

        return {
          success: false,
          error: errorMessage,
        }
      }
    } catch (error: any) {
      console.error('deleteTechnicalDocument exception:', error)
      return {
        success: false,
        error: error.message || 'Failed to delete technical document',
      }
    }
  },

  // Upload flight documents
  uploadFlightDocuments: async (payload: {
    userId: string
    bookingId: string
    documents: File[]
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('uploadFlightDocuments API call with payload:', {
        userId: payload.userId,
        bookingId: payload.bookingId,
        documentsCount: payload.documents.length,
      })

      const formData = new FormData()
      formData.append('userId', payload.userId)
      formData.append('bookingId', payload.bookingId)

      // Append all documents
      payload.documents.forEach(file => {
        formData.append('documents', file)
      })

      const response = await apiHelpers.postMultipart(
        '/api/advancing/event/booking/upload-flight-documents',
        formData
      )

      console.log('uploadFlightDocuments API response:', response)

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        let errorMessage = 'Failed to upload flight documents'
        if (response?.message) {
          if (Array.isArray(response.message)) {
            errorMessage = response.message[0] || errorMessage
          } else if (typeof response.message === 'string') {
            errorMessage = response.message
          }
        }

        console.error('uploadFlightDocuments failed:', {
          statusCode: response?.statusCode,
          message: response?.message,
          fullResponse: response,
        })

        return {
          success: false,
          error: errorMessage,
        }
      }
    } catch (error: any) {
      console.error('uploadFlightDocuments exception:', error)
      return {
        success: false,
        error: error.message || 'Failed to upload flight documents',
      }
    }
  },

  // Delete flight document
  deleteFlightDocument: async (payload: {
    userId: string
    flightDocumentId: string
  }): Promise<ApiResponse<any>> => {
    try {
      console.log('deleteFlightDocument API call with payload:', payload)

      const response = await apiHelpers.post(
        '/api/advancing/event/booking/delete-flight-document',
        payload
      )

      console.log('deleteFlightDocument API response:', response)

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        let errorMessage = 'Failed to delete flight document'
        if (response?.message) {
          if (Array.isArray(response.message)) {
            errorMessage = response.message[0] || errorMessage
          } else if (typeof response.message === 'string') {
            errorMessage = response.message
          }
        }

        console.error('deleteFlightDocument failed:', {
          statusCode: response?.statusCode,
          message: response?.message,
          fullResponse: response,
        })

        return {
          success: false,
          error: errorMessage,
        }
      }
    } catch (error: any) {
      console.error('deleteFlightDocument exception:', error)
      return {
        success: false,
        error: error.message || 'Failed to delete flight document',
      }
    }
  },

  // Check flight status
  checkFlight: async (payload: {
    userId: string
    bookingId: string
    flightType: 1 | 2 // 1 = arrival, 2 = departure
    flightNumber: string
    date: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/advancing/event/booking/check-flight',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to check flight',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to check flight',
      }
    }
  },
}

// Artist API services
export const artistService = {
  // Get all artists
  getAllArtists: async (
    userId: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<any>> => {
    try {
      const queryParams = new URLSearchParams({ userId })
      if (page !== undefined) queryParams.append('page', page.toString())
      if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString())

      const response = await apiHelpers.get(`/api/artist/get-profiles?${queryParams.toString()}`)

      // axios.ts returns raw response for /api/artist/* endpoints
      if (response.statusCode === 200) {
        return {
          success: true,
          data: {
            profiles: (response as any).profiles || [],
            pagination: (response as any).pagination || null,
          },
        }
      } else {
        return {
          success: false,
          error: (response as any).message?.[0] || 'Failed to fetch artists',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch artists',
      }
    }
  },

  // Get artist profile by ID
  getArtistProfile: async (artistProfileId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get(
        `/api/artist/get-profile?artistProfileId=${artistProfileId}`
      )

      // axios.ts returns raw response for /api/artist/* endpoints
      if (response.statusCode === 200) {
        return {
          success: true,
          data: (response as any).profile || null,
        }
      } else {
        return {
          success: false,
          error: (response as any).message?.[0] || 'Failed to fetch artist profile',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch artist profile',
      }
    }
  },

  // Create new artist
  createArtist: async (payload: any): Promise<ApiResponse<any>> => {
    try {
      // Add documents as empty array if not provided
      const requestPayload = {
        ...payload,
        documents: payload.documents || [],
      }
      const response = await apiHelpers.post(
        '/api/artist/profile-reg',
        requestPayload
      )

      // Check for statusCode 200 or 201 (successful creation)
      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        (response.statusCode === 200 || response.statusCode === 201)
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to create artist',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create artist',
      }
    }
  },

  // Update artist basic info with document upload support
  updateArtistBasicInfo: async (payload: {
    userId: string
    artistProfileId: string
    legalName: string
    stageName: string
    contactName?: string
    email: string
    phone: string
    instagramUrl?: string
    legalDocumentNumber?: string
    idExpirationDate: string
    newDocuments?: File[]
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('UserId', payload.userId)
      formData.append('ArtistProfileId', payload.artistProfileId)
      formData.append('LegalName', payload.legalName)
      formData.append('StageName', payload.stageName)

      // Contact name is optional
      if (payload.contactName) {
        formData.append('ContactName', payload.contactName)
      }

      formData.append('Email', payload.email)
      formData.append('Phone', payload.phone)

      // Instagram URL is optional
      if (payload.instagramUrl) {
        formData.append('InstagramUrl', payload.instagramUrl)
      }

      // Legal document number is optional
      if (payload.legalDocumentNumber) {
        formData.append('LegalDocumentNumber', payload.legalDocumentNumber)
      }

      formData.append('IdExpirationDate', payload.idExpirationDate)

      // Append documents if provided
      if (payload.newDocuments && payload.newDocuments.length > 0) {
        payload.newDocuments.forEach(file => {
          formData.append('NewDocuments', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/artist/update-basic-info',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update artist info',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update artist info',
      }
    }
  },

  // Add artist technical requirement with document upload support
  addTechnicalRequirement: async (payload: {
    userId: string
    artistProfileId: string
    mixerType: string
    turntables2x: string
    additionalTurntable2x: string
    comment: string
    documents?: File[]
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('UserId', payload.userId)
      formData.append('ArtistProfileId', payload.artistProfileId)
      formData.append('MixerType', payload.mixerType)
      formData.append('Turntables2x', payload.turntables2x)
      formData.append('AdditionalTurntable2x', payload.additionalTurntable2x)
      formData.append('Comment', payload.comment)

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('Documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/artist/add-technical-requirement',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to add technical requirement',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to add technical requirement',
      }
    }
  },

  // Update existing technical requirement with document upload support
  updateTechnicalRequirement: async (payload: {
    userId: string
    artistProfileId: string
    technicalRequirementId: string
    mixerType: string
    turntables2x: string
    additionalTurntable2x: string
    comment: string
    documents?: File[]
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('UserId', payload.userId)
      formData.append('ArtistProfileId', payload.artistProfileId)
      formData.append('TechnicalRequirementId', payload.technicalRequirementId)
      formData.append('MixerType', payload.mixerType)
      formData.append('Turntables2x', payload.turntables2x)
      formData.append('AdditionalTurntable2x', payload.additionalTurntable2x)
      formData.append('Comment', payload.comment)

      // Append documents if provided
      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(file => {
          formData.append('Documents', file)
        })
      }

      const response = await apiHelpers.postMultipart(
        '/api/artist/update-technical-requirement',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update technical requirement',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update technical requirement',
      }
    }
  },

  // Update artist technical requirements
  updateArtistTechnicalRequirements: async (
    payload: any
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.put(
        `/api/advancing/artists/${payload.artistId}/technical-requirements`,
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] ||
            'Failed to update technical requirements',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update technical requirements',
      }
    }
  },

  // Update artist rider notes
  updateArtistRiderNotes: async (payload: {
    userId: string
    artistProfileId: string
    value: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/artist/update-rider-notes',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update rider notes',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update rider notes',
      }
    }
  },

  // Update food & beverages restrictions
  updateArtistFoodRestrictions: async (payload: {
    userId: string
    artistProfileId: string
    value: string
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/artist/update-food-restrictions',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error:
            response?.message?.[0] || 'Failed to update food restrictions',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update food restrictions',
      }
    }
  },

  // Update artist agents
  updateArtistAgents: async (payload: {
    userId: string
    artistProfileId: string
    agentEmails: string[]
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.post(
        '/api/artist/update-agents',
        payload
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update agents',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update agents',
      }
    }
  },

  // Update artist profile image
  updateArtistProfileImage: async (payload: {
    userId: string
    artistProfileId: string
    image: File
  }): Promise<ApiResponse<any>> => {
    try {
      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('UserId', payload.userId)
      formData.append('ArtistProfileId', payload.artistProfileId)
      formData.append('Image', payload.image)

      const response = await apiHelpers.postMultipart(
        '/api/artist/update-profile-image',
        formData
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to update profile image',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update profile image',
      }
    }
  },

  // Delete artist
  deleteArtist: async (artistId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.delete(
        `/api/advancing/artists/${artistId}`
      )

      if (
        response &&
        typeof response === 'object' &&
        'statusCode' in response &&
        response.statusCode === 200
      ) {
        return {
          success: true,
          data: response,
        }
      } else {
        return {
          success: false,
          error: response?.message?.[0] || 'Failed to delete artist',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete artist',
      }
    }
  },

  // Search artist profiles
  searchArtistProfiles: async (searchTerm: string, userId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get(
        `/api/artist/get-profiles?Search=${encodeURIComponent(searchTerm)}&userId=${userId}`
      )

      // axios.ts returns raw response for /api/artist/* endpoints
      if (response.statusCode === 200) {
        return {
          success: true,
          data: {
            profiles: (response as any).profiles || [],
            pagination: (response as any).pagination || null,
          },
        }
      } else {
        return {
          success: false,
          error: (response as any).message?.[0] || 'Failed to search artists',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to search artists',
      }
    }
  },

  // Get agents for a specific artist profile
  getArtistAgents: async (artistProfileId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get(
        `/api/artist/get-agents?ArtistProfileId=${artistProfileId}`
      )

      // axios.ts returns raw response for /api/artist/* endpoints
      if (response.statusCode === 200) {
        return {
          success: true,
          data: {
            agents: (response as any).agents || [],
          },
        }
      } else {
        return {
          success: false,
          error: (response as any).message?.[0] || 'Failed to fetch artist agents',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch artist agents',
      }
    }
  },

  // Toggle artist profile status (active/inactive)
  toggleArtistStatus: async (payload: {
    userId: string
    artistProfileId: string
  }): Promise<ApiResponse<{ newStatus: number }>> => {
    try {
      const response = await apiHelpers.post('/api/artist/toggle-profile-status', payload)

      // axios.ts returns raw response for /api/artist/* endpoints
      if (response && typeof response === 'object' &&
          'statusCode' in response && response.statusCode === 200) {
        return {
          success: true,
          data: {
            newStatus: (response as any).newStatus,
          },
        }
      } else {
        return {
          success: false,
          error: (response as any).message?.[0] || 'Failed to toggle artist status',
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to toggle artist status',
      }
    }
  },
}

// Agent Services
export const agentService = {
  /**
   * Get minimal list of agents
   * @returns Promise<ApiResponse<AgentMinimal[]>>
   */
  getMinimalList: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get<any>('/api/agent/list-minimal')

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data.agents || [],
          statusCode: response.statusCode,
        }
      }

      return {
        success: false,
        error: response.error || 'Failed to fetch agents',
      }
    } catch (error: any) {
      console.error('Error fetching agents:', error)
      return {
        success: false,
        error: error.message || 'Failed to fetch agents',
      }
    }
  },

  /**
   * Search agents by name or email
   * @param searchTerm - The search query
   * @returns Promise<ApiResponse<AgentMinimal[]>>
   */
  searchAgents: async (searchTerm: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiHelpers.get<any>(
        `/api/agent/list-minimal?Search=${encodeURIComponent(searchTerm)}`
      )

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data.agents || [],
          statusCode: response.statusCode,
        }
      }

      return {
        success: false,
        error: response.error || 'Failed to search agents',
      }
    } catch (error: any) {
      console.error('Error searching agents:', error)
      return {
        success: false,
        error: error.message || 'Failed to search agents',
      }
    }
  },
}
